import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Mic2,
  Save,
  RotateCw,
  KeyRound,
  Check,
  Play,
  Pause,
} from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getStoredUser } from "@/lib/auth";

function Section({ eyebrow, title, subtitle, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl border border-[#5E594E] bg-[#5A5347] p-6"
    >
      <div className="mb-4">
        <p className="text-xs uppercase tracking-[0.22em] text-[#C9A961]">{eyebrow}</p>
        <h2 className="mt-1 font-serif text-2xl tracking-wide text-[#F5EFE2]">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-[#C9BEA8]">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

/* -------- Voice picker -------- */
function VoiceSection() {
  const [current, setCurrent] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [audioEl] = useState(() => (typeof window !== "undefined" ? new Audio() : null));
  const [voiceError, setVoiceError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, avail] = await Promise.all([
          api.get("/voice/config"),
          api.get("/voice/available"),
        ]);
        setCurrent(cfg.data);
        setVoices(avail.data.voices || []);
      } catch (e) {
        setVoiceError(readApiError(e));
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (audioEl) {
        audioEl.pause();
      }
    };
  }, [audioEl]);

  const preview = (v) => {
    if (!audioEl || !v?.preview_url) return;
    if (playing === v.voice_id) {
      audioEl.pause();
      setPlaying(null);
      return;
    }
    audioEl.src = v.preview_url;
    audioEl.play().catch(() => {});
    setPlaying(v.voice_id);
    audioEl.onended = () => setPlaying(null);
  };

  const select = async (voice_id) => {
    setSaving(true);
    try {
      const r = await api.post("/voice/select", { voice_id });
      setCurrent((c) => ({ ...(c || {}), voice_id: r.data.voice_id, voice_name: r.data.voice_name, preview_url: r.data.preview_url }));
      toast.success("Voice updated", { description: r.data.voice_name });
    } catch (e) {
      toast.error("Selection failed", { description: readApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#9A8E78]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading voices…
      </div>
    );
  }
  if (voiceError) {
    return (
      <p className="text-sm text-[#B85A4A]" data-testid="settings-voice-error">
        {voiceError}
      </p>
    );
  }
  return (
    <div className="space-y-4" data-testid="settings-voice">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#5E594E] bg-[#3D3A33] p-3">
        <Mic2 className="h-4 w-4 text-[#C9A961]" />
        <p className="text-sm text-[#F5EFE2]">
          Currently using{" "}
          <span className="font-serif text-lg text-[#C9A961]">
            {current?.voice_name || "—"}
          </span>
        </p>
        <span className="text-[10px] uppercase tracking-wider text-[#9A8E78]">
          {current?.voice_id?.slice(0, 8) || ""}
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {voices.map((v) => {
          const active = v.voice_id === current?.voice_id;
          const isPlaying = playing === v.voice_id;
          return (
            <div
              key={v.voice_id}
              data-testid={`voice-card-${v.voice_id.slice(0, 8)}`}
              className={`rounded-xl border p-3 transition-colors ${
                active
                  ? "border-[#C9A961] bg-[#C9A961]/10"
                  : "border-[#5E594E] bg-[#3D3A33] hover:border-[#C9A961]/50"
              }`}
            >
              <div className="mb-2 flex items-center justify-between">
                <p className="font-serif text-lg tracking-wide text-[#F5EFE2]">{v.name}</p>
                {active && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C9A961]">
                    <Check className="h-3 w-3" /> Active
                  </span>
                )}
              </div>
              <p className="mb-3 text-[10px] uppercase tracking-wider text-[#9A8E78]">
                {v.voice_id.slice(0, 12)}…
              </p>
              <div className="flex gap-2">
                {v.preview_url && (
                  <button
                    type="button"
                    onClick={() => preview(v)}
                    data-testid={`voice-preview-${v.voice_id.slice(0, 8)}`}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-[#5E594E] px-2 py-1 text-xs text-[#C9BEA8] hover:border-[#C9A961]/60 hover:text-[#C9A961]"
                  >
                    {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                    {isPlaying ? "Stop" : "Preview"}
                  </button>
                )}
                {!active && (
                  <button
                    type="button"
                    onClick={() => select(v.voice_id)}
                    disabled={saving}
                    data-testid={`voice-select-${v.voice_id.slice(0, 8)}`}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-[#C9A961] bg-[#C9A961] px-2 py-1 text-xs text-[#3D3A33] hover:bg-[#DDBE7A] disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Use this"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------- Brand voice prompt editor -------- */
function BrandVoiceSection() {
  const [value, setValue] = useState("");
  const [source, setSource] = useState("default");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultCopy, setDefaultCopy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/settings/brand-voice");
        setValue(r.data.value || "");
        setSource(r.data.source || "default");
        if ((r.data.source || "default") === "default") setDefaultCopy(r.data.value);
      } catch (e) {
        setError(readApiError(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/brand-voice", { value: value.trim() });
      setValue(r.data.value);
      setSource(r.data.source);
      toast.success("Brand voice saved", {
        description: "Every AI endpoint now uses this prompt.",
      });
    } catch (e) {
      toast.error("Save failed", { description: readApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const restore = async () => {
    if (!window.confirm("Restore the built-in default brand-voice prompt?")) return;
    if (!defaultCopy) {
      // Fetch again with source=default hint by clearing local first
      try {
        const r = await api.get("/admin/settings/brand-voice");
        setDefaultCopy(r.data.value);
        setValue(r.data.value);
      } catch (e) {
        toast.error("Could not fetch default", { description: readApiError(e) });
      }
      return;
    }
    setValue(defaultCopy);
    toast.info("Default restored — remember to Save.");
  };

  if (!loaded) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#9A8E78]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading brand voice…
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-[#B85A4A]" data-testid="settings-brandvoice-error">
        {error}
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="settings-brand-voice">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#9A8E78]">
        Source:
        <span
          className={`rounded-full border px-2 py-0.5 ${
            source === "custom"
              ? "border-[#C9A961] text-[#C9A961]"
              : "border-[#5E594E] text-[#C9BEA8]"
          }`}
        >
          {source === "custom" ? "custom (saved)" : "built-in default"}
        </span>
      </div>
      <Textarea
        data-testid="brand-voice-textarea"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={18}
        className="w-full border-[#5E594E] bg-[#3D3A33] font-mono text-xs leading-relaxed text-[#F5EFE2] placeholder:text-[#9A8E78]"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-[#9A8E78]">
          Used by every AI endpoint: Composer, Calendar, and all 3 Playbooks.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={restore}
            data-testid="brand-voice-restore"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#5E594E] px-3 py-1.5 text-xs text-[#C9BEA8] hover:border-[#C9A961]/60 hover:text-[#C9A961]"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Restore default
          </button>
          <Button
            type="button"
            onClick={save}
            disabled={saving || value.trim().length < 20}
            data-testid="brand-voice-save"
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save brand voice
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------- Change password -------- */
function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be ≥ 8 characters");
    if (next !== confirmPw) return toast.error("New password and confirmation don't match");
    setSaving(true);
    try {
      await api.post("/admin/settings/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed", { description: "Use the new password on next sign-in." });
      setCurrent("");
      setNext("");
      setConfirmPw("");
    } catch (e2) {
      toast.error("Change failed", { description: readApiError(e2) });
    } finally {
      setSaving(false);
    }
  };

  const user = getStoredUser();
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="settings-password">
      <p className="text-xs text-[#9A8E78]">
        Signed in as <span className="text-[#F5EFE2]">{user?.email || "—"}</span>
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <Label htmlFor="cur-pw" className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">
            Current password
          </Label>
          <Input
            id="cur-pw"
            data-testid="pw-current"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
        </div>
        <div>
          <Label htmlFor="new-pw" className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">
            New password
          </Label>
          <Input
            id="new-pw"
            data-testid="pw-new"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
        </div>
        <div>
          <Label htmlFor="conf-pw" className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">
            Confirm new
          </Label>
          <Input
            id="conf-pw"
            data-testid="pw-confirm"
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={8}
            className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2] placeholder:text-[#9A8E78]"
          />
        </div>
      </div>
      <div>
        <Button
          type="submit"
          disabled={saving || !current || !next || !confirmPw}
          data-testid="pw-submit"
          className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Changing…
            </>
          ) : (
            <>
              <KeyRound className="h-4 w-4" />
              Change password
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export default function AdminSettings() {
  return (
    <div className="space-y-8" data-testid="admin-settings">
      <div>
        <p className="admin-eyebrow">Settings</p>
        <h1 className="admin-h1 mt-2">
          Tune the workshop.
        </h1>
        <p className="mt-2 text-sm text-[#C9BEA8]">
          Voice, brand-voice prompt, account. Everything the AI reads from lives here.
        </p>
      </div>

      <Section
        eyebrow="Voice"
        title="Kenny's voice"
        subtitle="Currently reads every voiceover you generate. Pick a warmer, calmer, or steadier reading if you like."
        testid="section-voice"
      >
        <VoiceSection />
      </Section>

      <Section
        eyebrow="Brand Voice"
        title="The AI system prompt"
        subtitle="Single source of truth. Edit this and every AI-written post, script, and playbook adopts the change immediately."
        testid="section-brand-voice"
      >
        <BrandVoiceSection />
      </Section>

      <Section
        eyebrow="Account"
        title="Change password"
        subtitle="Recommended when a new device signs in or a team member rotates off."
        testid="section-password"
      >
        <PasswordSection />
      </Section>
    </div>
  );
}
